import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Headphones, ListOrdered, Repeat, Repeat1, SkipForward, Play, CheckSquare, Square } from "lucide-react";
import { api, getChildId } from "../lib/api";
import { loadRecordings, setAudioPrefs, stopAudio } from "../lib/audio";
import { StoryPlayer } from "../components/lesson/StoryPlayer";

const MODES = [
  ["sequence", "依序播到最後", "順序", ListOrdered],
  ["loopAll", "全部循環", "循環", Repeat],
  ["repeatOne", "單篇重複", "重複", Repeat1],
];

const Playlist = ({ stories, picked, toggle, current, startAt, sfx = "" }) => (
  <div className="space-y-2" data-testid={`listen-playlist${sfx}`}>
    {stories.map((s) => {
      const on = picked.has(s.story_id);
      return (
        <div key={s.story_id} className={`flex items-center gap-2 rounded-2xl border-2 px-2 py-2 ${current === s.story_id ? "border-[var(--vermilion)] bg-red-50" : "border-[var(--line)] bg-white"}`}>
          <button data-testid={`listen-pick-${s.story_id}${sfx}`} onClick={() => toggle(s.story_id)} className="w-10 h-10 flex items-center justify-center text-[var(--vermilion)] shrink-0">{on ? <CheckSquare /> : <Square className="text-stone-300" />}</button>
          <button data-testid={`listen-play-${s.story_id}${sfx}`} onClick={() => startAt(s.story_id)} className="flex-1 flex items-center gap-2 text-left min-w-0">
            <span className="text-2xl">{s.sticker}</span>
            <span className="font-black text-lg truncate">{s.story_number < 100 ? `${s.story_number}. ` : ""}{s.story_title}</span>
          </button>
        </div>
      );
    })}
  </div>
);

export default function Listen() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const hall = params.get("hall") === "life" ? "life" : "classic";
  const life = hall === "life";
  const [stories, setStories] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [mode, setMode] = useState("sequence");
  const [current, setCurrent] = useState(null);
  const [round, setRound] = useState(0);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    const childId = getChildId();
    if (childId) api.get("/children").then(({ data }) => setAudioPrefs(data.find((c) => c.child_id === childId)?.settings));
    loadRecordings();
    api.get(`/listen/stories?hall=${hall}`).then(({ data }) => {
      setStories(data);
      const start = params.get("start");
      setPicked(new Set(start ? [start] : data.map((s) => s.story_id)));
      if (start) setMode("repeatOne");
    });
    return () => stopAudio();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const queue = useMemo(() => stories.filter((s) => picked.has(s.story_id)), [stories, picked]);
  const story = stories.find((s) => s.story_id === current);

  const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const startAt = (id) => { setFinished(false); setCurrent(id); setRound((r) => r + 1); };
  const next = () => {
    const idx = queue.findIndex((s) => s.story_id === current);
    if (mode === "repeatOne") return startAt(current);
    const nxt = queue[idx + 1] || (mode === "loopAll" ? queue[0] : null);
    if (nxt) startAt(nxt.story_id);
    else { setCurrent(null); setFinished(true); }
  };

  return (
    <div className={`h-screen flex flex-col overflow-x-hidden ${life ? "life-bg font-round" : "paper-bg font-serif-tc"}`} data-testid="listen-page">
      <header className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-6 py-3 border-b-2 ${life ? "bg-white/80 border-[#cfe8e2]" : "bg-amber-100/90 border-amber-300/80"}`}>
        <button data-testid="listen-back-btn" onClick={() => nav(life ? "/life" : "/play")} className="w-12 h-12 shrink-0 rounded-full bg-white border-2 border-[var(--line)] flex items-center justify-center"><ArrowLeft /></button>
        <Headphones className={`hidden sm:block ${life ? "text-[#3E9C8F]" : "text-[var(--vermilion)]"}`} />
        <div className={`font-black text-xl sm:text-2xl whitespace-nowrap ${life ? "text-[#2c6e65]" : "text-[var(--tea)]"}`} data-testid="listen-hall-name">{life ? "生活中文館・只聽故事" : "只聽故事"}</div>
        {story && <div className="hidden md:block text-xl font-black text-[var(--vermilion)] ml-4" data-testid="listen-now-title">{story.story_title}</div>}
        <div className="ml-auto flex gap-1 sm:gap-2" data-testid="listen-modes">
          {MODES.map(([k, label, short, Icon]) => (
            <button key={k} data-testid={`listen-mode-${k}`} onClick={() => setMode(k)}
              className={`h-12 px-2 sm:px-4 rounded-full border-2 font-bold flex items-center gap-1 sm:gap-2 shrink-0 ${mode === k ? "bg-[var(--jade)] text-white border-transparent" : "bg-white border-[var(--line)] text-[var(--tea)]"}`}>
              <Icon size={20} /><span className="hidden sm:inline">{label}</span><span className="sm:hidden text-sm whitespace-nowrap">{short}</span>
            </button>
          ))}
        </div>
      </header>
      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4 p-3 sm:p-5">
        <aside className="hidden lg:flex flex-col min-h-0">
          <div className="flex gap-2 mb-3">
            <button data-testid="listen-select-all" onClick={() => setPicked(new Set(stories.map((s) => s.story_id)))} className="flex-1 h-10 rounded-full bg-white border-2 border-[var(--line)] font-bold text-sm">全選</button>
            <button data-testid="listen-select-none" onClick={() => setPicked(new Set())} className="flex-1 h-10 rounded-full bg-white border-2 border-[var(--line)] font-bold text-sm">清除</button>
          </div>
          <div className="overflow-y-auto pr-1"><Playlist stories={stories} picked={picked} toggle={toggle} current={current} startAt={startAt} /></div>
        </aside>
        <section className="min-h-0 min-w-0 flex flex-col">
          {story ? (
            <>
              <div className="md:hidden font-black text-xl text-[var(--vermilion)] mb-2" data-testid="listen-now-title-mobile">{story.story_title}</div>
              <div className="flex-1 min-h-0">
                <StoryPlayer key={`${story.story_id}-${round}`} title={story.story_title} scenes={story.scenes} continuous onDone={next} />
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <button data-testid="listen-stop-btn" onClick={() => { stopAudio(); setCurrent(null); }} className="h-12 px-5 rounded-full bg-white border-2 border-[var(--line)] font-bold">選故事</button>
                <button data-testid="listen-next-story-btn" onClick={next} className="h-12 px-5 rounded-full bg-white border-2 border-[var(--line)] font-bold flex items-center gap-2"><SkipForward size={20} /> 下一個故事</button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center lg:justify-center text-center gap-6 scroll-card p-6 sm:p-8 overflow-y-auto" data-testid="listen-start-panel">
              <Headphones size={72} className="text-[var(--vermilion)]" />
              <div className="text-2xl sm:text-3xl font-black">{finished ? "故事都聽完了！" : <>舒服地坐好，<br className="sm:hidden" />一起聽故事</>}</div>
              <p className="text-stone-600">已選 {queue.length} 個故事，畫面會自動往下播放，不用按繼續。</p>
              <button data-testid="listen-start-btn" disabled={!queue.length} onClick={() => startAt(queue[0].story_id)} className="kid-btn px-10 bg-[var(--vermilion)] text-white text-2xl flex items-center gap-3 disabled:opacity-40"><Play /> {finished ? "再聽一次" : "開始播放"}</button>
              <div className="lg:hidden w-full max-w-md text-left"><Playlist sfx="-m" stories={stories} picked={picked} toggle={toggle} current={current} startAt={startAt} /></div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
